import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

// 全域管理避免多重彈窗時捲動鎖定與堆疊錯亂
const scrollLockCounter = ref(0)
let stackIdSeed = 0
const stackOrder = ref([])

const focusableSelectors = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
]

export default function useModalController(props, emit) {
  const overlayRef = ref(null)
  const dialogRef = ref(null)

  const rendered = ref(props.modelValue || props.open || props.visible || false)
  const isResizing = ref(false)
  const resizeStart = ref({ x: 0, y: 0, width: 0, height: 0 })
  const size = ref({ width: null, height: null })
  const isMobile = ref(false)
  const stackId = ++stackIdSeed

  const parsedWidth = computed(() => {
    if (!props.width) return '600px'
    if (typeof props.width === 'number') return `${props.width}px`
    return props.width
  })

  const activeOpen = computed({
    get() {
      return props.modelValue ?? props.open ?? props.visible ?? false
    },
    set(value) {
      emit('update:open', value)
      emit('update:visible', value)
      emit('update:modelValue', value)
    }
  })

  const zIndex = computed(() => 1200 + stackOrder.value.indexOf(stackId) * 2)

  const overlayStyle = computed(() => ({
    zIndex: zIndex.value
  }))

  const dialogStyle = computed(() => {
    const widthValue = isMobile.value ? '95%' : parsedWidth.value
    const style = {
      width: widthValue
    }
    if (size.value.width) style.width = `${size.value.width}px`
    if (size.value.height) style.height = `${size.value.height}px`
    return style
  })

  const shouldShowFooter = computed(() => props.footer !== null)

  const attachStack = () => {
    // 確保最新的彈窗在堆疊頂部
    const existingIndex = stackOrder.value.indexOf(stackId)
    if (existingIndex !== -1) {
      stackOrder.value.splice(existingIndex, 1)
    }
    stackOrder.value.push(stackId)
  }

  const detachStack = () => {
    stackOrder.value = stackOrder.value.filter((id) => id !== stackId)
  }

  const lockScroll = () => {
    if (!props.lockScroll) return
    scrollLockCounter.value += 1
    if (scrollLockCounter.value === 1) {
      document.body.dataset.prevOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
  }

  const unlockScroll = () => {
    if (!props.lockScroll) return
    scrollLockCounter.value = Math.max(0, scrollLockCounter.value - 1)
    if (scrollLockCounter.value === 0) {
      document.body.style.overflow = document.body.dataset.prevOverflow || ''
      delete document.body.dataset.prevOverflow
    }
  }

  const setFocusTrap = () => {
    if (!props.trapFocus) return
    nextTick(() => {
      const el = dialogRef.value
      if (!el) return
      const focusables = el.querySelectorAll(focusableSelectors.join(','))
      if (focusables.length > 0) {
        focusables[0].focus({ preventScroll: true })
      } else {
        el.focus({ preventScroll: true })
      }
    })
  }

  const handleTabKey = (event) => {
    if (!props.trapFocus) return
    if (event.key !== 'Tab') return
    const el = dialogRef.value
    if (!el) return
    const focusables = Array.from(el.querySelectorAll(focusableSelectors.join(','))).filter(
      (node) => !node.hasAttribute('disabled')
    )
    if (focusables.length === 0) {
      event.preventDefault()
      el.focus({ preventScroll: true })
      return
    }
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus({ preventScroll: true })
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus({ preventScroll: true })
    }
  }

  const handleEsc = (event) => {
    if (!props.escClosable) return
    if (event.key === 'Escape' && activeOpen.value) {
      // 只有當前彈窗是最上層時才關閉
      if (stackOrder.value[stackOrder.value.length - 1] === stackId) {
        event.stopPropagation()
        emit('cancel', 'esc')
        activeOpen.value = false
      }
    }
  }

  const onMaskClick = (event) => {
    if (!props.maskClosable) return
    if (event.target === overlayRef.value) {
      emit('cancel', 'mask')
      activeOpen.value = false
    }
  }

  const onOk = () => {
    if (props.confirmLoading || props.okButtonProps?.disabled) return
    emit('ok')
  }

  const onCancel = () => {
    emit('cancel', 'button')
    activeOpen.value = false
  }

  const startResizing = (event) => {
    if (!props.resizable) return
    if (event.button !== 0) return
    isResizing.value = true
    const rect = dialogRef.value?.getBoundingClientRect()
    resizeStart.value = {
      x: event.clientX,
      y: event.clientY,
      width: rect?.width || 0,
      height: rect?.height || 0
    }
    window.addEventListener('pointermove', handleResizing)
    window.addEventListener('pointerup', stopResizing)
  }

  const handleResizing = (event) => {
    if (!isResizing.value) return
    const deltaX = event.clientX - resizeStart.value.x
    const deltaY = event.clientY - resizeStart.value.y
    const minWidth = 320
    const minHeight = 200
    size.value = {
      width: Math.max(minWidth, resizeStart.value.width + deltaX),
      height: Math.max(minHeight, resizeStart.value.height + deltaY)
    }
  }

  const stopResizing = () => {
    isResizing.value = false
    window.removeEventListener('pointermove', handleResizing)
    window.removeEventListener('pointerup', stopResizing)
  }

  const handleKeydown = (event) => {
    handleTabKey(event)
    handleEsc(event)
  }

  const updateViewport = () => {
    isMobile.value = window.innerWidth <= 768
  }

  watch(
    () => activeOpen.value,
    (isOpen) => {
      if (isOpen) {
        rendered.value = true
        attachStack()
        lockScroll()
        setFocusTrap()
        nextTick(() => {
          if (dialogRef.value) {
            dialogRef.value.addEventListener('keydown', handleKeydown)
          }
        })
      } else {
        detachStack()
        unlockScroll()
        if (dialogRef.value) {
          dialogRef.value.removeEventListener('keydown', handleKeydown)
        }
        if (props.destroyOnClose) {
          // 如果 destroyOnClose 為 true，延遲隱藏以顯示動畫
          setTimeout(() => {
            if (!activeOpen.value) {
              rendered.value = false
              size.value = { width: null, height: null }
            }
          }, 200)
        } else {
          // 如果 destroyOnClose 為 false，立即隱藏
          rendered.value = false
          size.value = { width: null, height: null }
        }
      }
    },
    { immediate: true }
  )

  onMounted(() => {
    updateViewport()
    window.addEventListener('resize', updateViewport)
    document.addEventListener('keydown', handleEsc)
  })

  onBeforeUnmount(() => {
    detachStack()
    unlockScroll()
    window.removeEventListener('resize', updateViewport)
    document.removeEventListener('keydown', handleEsc)
    window.removeEventListener('pointermove', handleResizing)
    window.removeEventListener('pointerup', stopResizing)
  })

  return {
    overlayRef,
    dialogRef,
    rendered,
    activeOpen,
    overlayStyle,
    dialogStyle,
    shouldShowFooter,
    isMobile,
    onMaskClick,
    onOk,
    onCancel,
    startResizing
  }
}

